import React, { useCallback, useEffect, useState } from "react";
import api, { errMsg } from "../api";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { Users, Save, Sparkles, Power, PowerOff, RefreshCw } from "lucide-react";

/**
 * MailPoolAdmin — Admin/Master configuration for the AI mail-distribution pool.
 * When enabled, new tasks from shared mailboxes are auto-assigned to the pool
 * member with the lowest active workload. Admin/Master can:
 *   - toggle the pool on/off
 *   - pick which users are in the pool
 *   - see each member's current workload
 */
export default function MailPoolAdmin({ lang = "de" }) {
    const t = (de, en) => (lang === "de" ? de : en);
    const [enabled, setEnabled] = useState(false);
    const [memberIds, setMemberIds] = useState([]);
    const [allUsers, setAllUsers] = useState([]);
    const [workload, setWorkload] = useState([]);
    const [busy, setBusy] = useState(false);
    const [dirty, setDirty] = useState(false);

    const load = useCallback(async () => {
        try {
            const [pool, users, wl] = await Promise.all([
                api.get("/ms/pool"),
                api.get("/users"),
                api.get("/ms/pool/workload"),
            ]);
            setEnabled(!!pool.data?.enabled);
            setMemberIds(pool.data?.member_user_ids || []);
            setAllUsers(users.data || []);
            setWorkload(wl.data || []);
            setDirty(false);
        } catch (e) { toast.error(errMsg(e)); }
    }, []);

    useEffect(() => { load(); }, [load]);

    const toggleMember = (uid) => {
        setMemberIds((prev) => prev.includes(uid) ? prev.filter((x) => x !== uid) : [...prev, uid]);
        setDirty(true);
    };

    const save = async () => {
        setBusy(true);
        try {
            await api.put("/ms/pool", { enabled, member_user_ids: memberIds });
            toast.success(t("Pool gespeichert", "Pool saved"));
            await load();
        } catch (e) { toast.error(errMsg(e)); }
        finally { setBusy(false); }
    };

    const workloadFor = (uid) => workload.find((w) => w.user_id === uid)?.active_tasks ?? null;

    return (
        <div data-testid="mail-pool-admin">
            {/* Header + toggle */}
            <div className="flex items-start justify-between mb-3 gap-4">
                <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center flex-shrink-0">
                        <Sparkles className="w-4 h-4 text-white" />
                    </div>
                    <div>
                        <p className="text-sm font-semibold">{t("KI-Verteilungs-Pool", "AI distribution pool")}</p>
                        <p className="text-xs text-neutral-500 mt-0.5">
                            {t("Neue Mails aus geteilten Postfächern werden automatisch dem Pool-Mitglied mit der geringsten Auslastung zugewiesen.",
                               "New mails from shared mailboxes are auto-assigned to the pool member with the lowest workload.")}
                        </p>
                    </div>
                </div>
                <button
                    onClick={() => { setEnabled((v) => !v); setDirty(true); }}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-xs font-semibold transition flex-shrink-0 ${
                        enabled ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-200" : "bg-neutral-100 text-neutral-600 hover:bg-neutral-200"
                    }`}
                    data-testid="pool-toggle"
                >
                    {enabled ? <Power className="w-3.5 h-3.5" /> : <PowerOff className="w-3.5 h-3.5" />}
                    {enabled ? t("Aktiv", "Active") : t("Aus", "Off")}
                </button>
            </div>

            {/* Member list with workload */}
            <div className="rounded-lg border border-neutral-200 overflow-hidden">
                <div className="bg-neutral-50 px-3 py-2 flex items-center justify-between border-b border-neutral-200">
                    <div className="flex items-center gap-1.5 text-xs font-semibold text-neutral-700">
                        <Users className="w-3.5 h-3.5" />
                        {t("Pool-Mitglieder", "Pool members")} ({memberIds.length})
                    </div>
                    <button onClick={load} className="text-neutral-400 hover:text-neutral-700" title={t("Aktualisieren", "Refresh")} data-testid="pool-refresh">
                        <RefreshCw className="w-3.5 h-3.5" />
                    </button>
                </div>
                <div className="max-h-72 overflow-y-auto divide-y divide-neutral-100">
                    {allUsers.length === 0 && (
                        <p className="px-4 py-6 text-xs text-neutral-400 italic text-center">{t("Keine Nutzer gefunden", "No users found")}</p>
                    )}
                    {allUsers.map((u) => {
                        const checked = memberIds.includes(u.id);
                        const wl = workloadFor(u.id);
                        return (
                            <label
                                key={u.id}
                                className={`flex items-center gap-2.5 px-3 py-2 text-xs cursor-pointer hover:bg-neutral-50 transition ${checked ? "bg-blue-50/50" : ""}`}
                                data-testid={`pool-member-${u.id}`}
                            >
                                <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={() => toggleMember(u.id)}
                                    data-testid={`pool-toggle-${u.id}`}
                                />
                                <div className="flex-1 min-w-0">
                                    <p className="font-semibold text-neutral-800 truncate">{u.name}</p>
                                    <p className="text-[10px] text-neutral-500 truncate">{u.email}</p>
                                </div>
                                <span className="text-[10px] text-neutral-400 uppercase tracking-wider">{u.role}</span>
                                {checked && wl !== null && (
                                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                                        wl === 0 ? "bg-emerald-100 text-emerald-700" :
                                        wl < 5 ? "bg-blue-100 text-blue-700" :
                                        wl < 15 ? "bg-amber-100 text-amber-700" :
                                        "bg-red-100 text-red-700"
                                    }`}>
                                        {wl} {t("offen", "open")}
                                    </span>
                                )}
                            </label>
                        );
                    })}
                </div>
            </div>

            <div className="flex justify-end mt-3">
                <Button
                    onClick={save}
                    disabled={busy || !dirty}
                    className="bg-brand-500 hover:bg-brand-600 text-white gap-1.5"
                    data-testid="pool-save"
                >
                    <Save className="w-3.5 h-3.5" />
                    {busy ? t("Speichere…", "Saving…") : t("Speichern", "Save")}
                </Button>
            </div>
        </div>
    );
}
